import React, { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ChevronDown, Plus, Trash2, Pencil, Archive, RotateCcw, Users, User, Building2,
  ListPlus, Cpu, Clock, GitBranch,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = '/api';

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  if (n === 0) return '-';
  const s = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(1)}K`;
  return `${s}$${a.toFixed(0)}`;
};

const naturalCompare = (a, b) => {
  const ax = (a || '').match(/^(\d+)/);
  const bx = (b || '').match(/^(\d+)/);
  if (ax && bx) {
    const diff = parseInt(ax[1], 10) - parseInt(bx[1], 10);
    if (diff !== 0) return diff;
  }
  return (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });
};

const FORECAST_RULES = [
  { value: 'fixed', label: 'Fixed Monthly Cost' },
  { value: 'per_person_all', label: 'Per Person (All Staff)' },
  { value: 'per_person_team', label: 'Per Person (Specific Teams)' },
  { value: 'per_person_users', label: 'Per Person (Specific Users)' },
];

const SCENARIOS = ['Base', 'Conservative', 'Aggressive', 'Raise Now', 'Raise Later'];

/**
 * Reusable component for expense category forecast driver sections.
 * Used for: Digital Tools, Operations, Professional Services, etc.
 */
export default function CategoryDriverSection({
  sectionKey,
  categoryActuals,      // { subType: [{ item_key, item_name, vendor, buckets, status }] }
  drivers,              // All drivers (filtered by caller for this section's driver_type)
  buckets,
  cutoffIdx,
  grouping,
  bucketLabel,
  computeDriverRow,
  catCodeMap,
  workspaceId,
  vendors,              // refVendors list
  orgMembers,
  staffingActuals,
  onDriverSaved,
  onDriverDeleted,
}) {
  const [expandedSubs, setExpandedSubs] = useState(null); // null = all expanded
  const [showArchived, setShowArchived] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [form, setForm] = useState({});
  const [driverTab, setDriverTab] = useState('manual'); // manual | automated | history | scenario

  const DRIVER_TABS = [
    { id: 'manual', label: 'Manual Lines', icon: ListPlus },
    { id: 'automated', label: 'Automated / Modeled', icon: Cpu },
    { id: 'history', label: 'History', icon: Clock },
    { id: 'scenario', label: 'Scenario', icon: GitBranch },
  ];

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Active drivers for this section
  const activeDrivers = useMemo(() =>
    drivers.filter(d => (d.item_status || 'active') === 'active'),
    [drivers]
  );
  const retiredDrivers = useMemo(() =>
    drivers.filter(d => d.item_status === 'retired'),
    [drivers]
  );

  // Group actuals by sub_type
  const actualsBySubType = useMemo(() => {
    const entries = Object.entries(categoryActuals || {});
    entries.sort((a, b) => naturalCompare(catCodeMap[a[0]] || a[0], catCodeMap[b[0]] || b[0]));
    return entries;
  }, [categoryActuals, catCodeMap]);

  // Group active drivers by sub_type
  const driversBySubType = useMemo(() => {
    const groups = {};
    for (const d of activeDrivers) {
      const sub = d.sub_type || d.tool_category || 'General';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(d);
    }
    return groups;
  }, [activeDrivers]);

  // Merged sub-type list (from actuals + drivers)
  const allSubTypes = useMemo(() => {
    const subs = new Set();
    for (const [sub] of actualsBySubType) subs.add(sub);
    for (const sub of Object.keys(driversBySubType)) subs.add(sub);
    return [...subs].sort((a, b) => naturalCompare(catCodeMap[a] || a, catCodeMap[b] || b));
  }, [actualsBySubType, driversBySubType, catCodeMap]);

  const toggleSub = (name) => {
    setExpandedSubs(prev => {
      if (prev === null) {
        const n = new Set(allSubTypes);
        n.delete(name);
        return n;
      }
      const n = new Set(prev);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };
  const isSubExpanded = (name) => expandedSubs === null || (expandedSubs && expandedSubs.has(name));

  // Save driver
  const saveDriver = async () => {
    const body = {
      ...form,
      driver_type: sectionKey,
      sub_type: form.sub_type || form.tool_category || '',
      tool_name: form.tool_name || form.name || '',
      tool_category: form.sub_type || form.tool_category || '',
      category_link: form.category_link || '',
    };
    if (editingDriver) body.driver_id = editingDriver.driver_id;
    try {
      const r = await fetch(`${API}/finance/pl/${workspaceId}/drivers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body),
      });
      if (r.ok) {
        // Bidirectional vendor sync: if vendor_name is new, create in vendors ref
        if (form.vendor_name && vendors && !vendors.find(v => v.name === form.vendor_name)) {
          try {
            await fetch(`${API}/finance/ref/${workspaceId}/vendors`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
              body: JSON.stringify({ name: form.vendor_name, category: sectionKey }),
            });
          } catch {}
        }
        toast.success(editingDriver ? 'Updated' : 'Added');
        setAddModal(false); setEditingDriver(null); setForm({});
        onDriverSaved?.();
      }
    } catch { toast.error('Failed'); }
  };

  const deleteDriver = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await fetch(`${API}/finance/pl/${workspaceId}/drivers/${id}`, { method: 'DELETE', credentials: 'include' });
    onDriverDeleted?.();
    toast.success('Deleted');
  };

  const retireDriver = async (id) => {
    await fetch(`${API}/finance/pl/${workspaceId}/drivers/${id}/retire`, { method: 'POST', credentials: 'include' });
    onDriverSaved?.();
    toast.success('Retired');
  };

  const reactivateDriver = async (id) => {
    await fetch(`${API}/finance/pl/${workspaceId}/drivers/${id}/reactivate`, { method: 'POST', credentials: 'include' });
    onDriverSaved?.();
    toast.success('Reactivated');
  };

  const openAdd = (subType) => {
    setEditingDriver(null);
    setForm({ sub_type: subType || '', forecast_rule: 'fixed' });
    setAddModal(true);
  };

  const openEdit = (driver) => {
    setEditingDriver(driver);
    setForm({ ...driver });
    setAddModal(true);
  };

  // Staff count for per-person calculations
  const totalStaffCount = useMemo(() => {
    return (staffingActuals?.staff || []).filter(s => !s.is_archived).length +
      drivers.filter(d => d.driver_type === 'staffing' && (d.item_status || 'active') === 'active').length;
  }, [staffingActuals, drivers]);

  const stickyLeftCls = "sticky left-0 z-10";

  return (
    <div className="border-t">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/20">
        <div className="flex items-center gap-3">
          {/* Driver sub-tabs */}
          <div className="flex items-center gap-0.5 bg-background border rounded-md p-0.5" data-testid={`${sectionKey}-driver-tabs`}>
            {DRIVER_TABS.map(dt => (
              <button
                key={dt.id}
                onClick={() => setDriverTab(dt.id)}
                className={cn(
                  "flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded transition-colors",
                  driverTab === dt.id
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
                data-testid={`${sectionKey}-driver-tab-${dt.id}`}
              >
                <dt.icon className="w-3 h-3" />{dt.label}
              </button>
            ))}
          </div>
        </div>
        {driverTab === 'manual' && (
          <Button size="sm" onClick={() => openAdd('')} data-testid={`add-${sectionKey}-driver-btn`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add Item
          </Button>
        )}
      </div>

      {/* Manual Lines tab */}
      {driverTab === 'manual' && (<>
      {/* Sub-type sections */}
      {allSubTypes.length > 0 ? (
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-slate-100 dark:bg-slate-900 sticky top-0 z-20">
              <tr>
                <th className={cn("p-2 text-left font-semibold min-w-[220px] border-r border-border bg-slate-100 dark:bg-slate-900 sticky top-0 left-0 z-40")}>
                  Item / Vendor
                </th>
                <th className="p-2 text-left font-medium min-w-[80px] sticky top-0 z-20 bg-slate-100 dark:bg-slate-900">Rule</th>
                {buckets.map((b, i) => (
                  <th key={b} className={cn("p-2 text-right font-medium min-w-[85px] whitespace-nowrap sticky top-0 z-20",
                    i <= cutoffIdx ? "bg-slate-100 dark:bg-slate-900" : "bg-blue-100 dark:bg-blue-950",
                    i === cutoffIdx && "border-r-2 border-amber-500"
                  )}>{bucketLabel(b, grouping)}</th>
                ))}
                <th className="p-2 text-right font-semibold min-w-[90px] bg-slate-100 dark:bg-slate-900 sticky top-0 z-20">Total</th>
                <th className="p-2 text-right w-20 bg-slate-100 dark:bg-slate-900 sticky top-0 z-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allSubTypes.map(subType => {
                const isExp = isSubExpanded(subType);
                const actualItems = (categoryActuals || {})[subType] || [];
                const driverItems = driversBySubType[subType] || [];

                // Sub-type totals across actuals + drivers
                const subTotals = buckets.map((b, i) => {
                  let total = actualItems.reduce((s, item) => s + (item.buckets[b] || 0), 0);
                  total += driverItems.reduce((s, d) => s + (computeDriverRow(d, buckets)[i] || 0), 0);
                  return total;
                });
                const subGrand = subTotals.reduce((a, v) => a + v, 0);

                return (
                  <React.Fragment key={subType}>
                    {/* Sub-type header */}
                    <tr className="bg-slate-50 dark:bg-slate-900/50 border-t cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                      onClick={() => toggleSub(subType)} data-testid={`cat-sub-${subType}`}>
                      <td className={cn("p-1.5 pl-2 border-r border-border bg-slate-50 dark:bg-slate-900/50", stickyLeftCls, "z-30")}>
                        <div className="flex items-center gap-1.5">
                          <ChevronDown className={cn("w-3 h-3 transition-transform", !isExp && "-rotate-90")} />
                          <span className="text-[10px] font-bold uppercase tracking-wider orka-text-secondary">
                            {catCodeMap[subType] && <span className="font-mono text-[9px] mr-1 opacity-70">{catCodeMap[subType]}</span>}
                            {subType} ({actualItems.length + driverItems.length})
                          </span>
                          <Button variant="ghost" size="icon" className="h-5 w-5 ml-1" title="Add item"
                            onClick={e => { e.stopPropagation(); openAdd(subType); }}>
                            <Plus className="w-2.5 h-2.5" />
                          </Button>
                        </div>
                      </td>
                      <td className="p-1.5 bg-slate-50 dark:bg-slate-900/50"></td>
                      {subTotals.map((val, i) => (
                        <td key={buckets[i]} className={cn("p-1.5 text-right font-mono text-[10px] font-semibold text-slate-600",
                          i === cutoffIdx && "border-r-2 border-amber-500"
                        )}>{val > 0 ? fmtMoney(val) : '-'}</td>
                      ))}
                      <td className="p-1.5 text-right font-mono text-[10px] font-bold orka-text-secondary">{fmtMoney(subGrand)}</td>
                      <td className="p-1.5"></td>
                    </tr>

                    {/* Individual items (actuals + drivers) */}
                    {isExp && (
                      <>
                        {/* Actual items from expense ledger */}
                        {actualItems.map((item, idx) => {
                          const rowTotal = buckets.reduce((s, b) => s + (item.buckets[b] || 0), 0);
                          return (
                            <tr key={`actual-${item.item_key || idx}`} className="border-t border-border/50 hover:bg-muted/10" data-testid={`cat-actual-row-${idx}`}>
                              <td className={cn("p-2 pl-6 border-r border-border bg-card", stickyLeftCls, "z-20")}>
                                <div className="font-medium text-foreground truncate max-w-[180px]" title={item.item_name}>
                                  {item.item_name}
                                </div>
                                {item.vendor && (
                                  <div className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                    <Building2 className="w-2.5 h-2.5" />{item.vendor}
                                  </div>
                                )}
                              </td>
                              <td className="p-2 text-[10px] text-muted-foreground">Actual</td>
                              {buckets.map((b, i) => {
                                const val = item.buckets[b] || 0;
                                return (
                                  <td key={b} className={cn("p-2 text-right font-mono",
                                    val > 0 && "text-foreground",
                                    val === 0 && "text-muted-foreground/30",
                                    i === cutoffIdx && "border-r-2 border-amber-500",
                                    i > cutoffIdx && "bg-blue-50/10 dark:bg-blue-950/5"
                                  )}>{val > 0 ? fmtMoney(val) : '-'}</td>
                                );
                              })}
                              <td className="p-2 text-right font-mono font-semibold">{fmtMoney(rowTotal)}</td>
                              <td className="p-2"></td>
                            </tr>
                          );
                        })}

                        {/* Forecast driver items */}
                        {driverItems.map((d, idx) => {
                          const contributions = computeDriverRow(d, buckets);
                          const total = contributions.reduce((s, v) => s + v, 0);
                          const ruleLabel = d.forecast_rule === 'per_person_all' ? 'All Staff' :
                            d.forecast_rule === 'per_person_team' ? 'By Team' :
                            d.forecast_rule === 'per_person_users' ? 'By Users' : 'Fixed';
                          return (
                            <tr key={d.driver_id || `drv-${idx}`} className="border-t border-border/50 hover:bg-muted/10 group bg-blue-50/20 dark:bg-blue-950/10" data-testid={`cat-driver-row-${d.driver_id}`}>
                              <td className={cn("p-2 pl-6 border-r border-border bg-blue-50/20 dark:bg-blue-950/10", stickyLeftCls, "z-20")}>
                                <div className="flex items-center gap-1">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-blue-700 dark:text-blue-300 truncate max-w-[160px]" title={d.tool_name || d.name}>
                                      {d.tool_name || d.name || 'Unnamed'}
                                    </div>
                                    {d.vendor_name && (
                                      <div className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                        <Building2 className="w-2.5 h-2.5" />{d.vendor_name}
                                      </div>
                                    )}
                                    {d.scenario && d.scenario !== 'base' && (
                                      <Badge variant="outline" className="text-[8px] px-1 py-0 mt-0.5">{d.scenario}</Badge>
                                    )}
                                  </div>
                                  <div className="flex gap-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900 text-muted-foreground hover:text-foreground"
                                      onClick={() => openEdit(d)} title="Edit"><Pencil className="w-3 h-3" /></button>
                                    <button className="p-0.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 text-muted-foreground hover:text-amber-500"
                                      onClick={() => retireDriver(d.driver_id)} title="Retire"><Archive className="w-3 h-3" /></button>
                                    <button className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500"
                                      onClick={() => deleteDriver(d.driver_id)} title="Delete"><Trash2 className="w-3 h-3" /></button>
                                  </div>
                                </div>
                              </td>
                              <td className="p-2 text-[10px] text-blue-600">
                                <div className="flex items-center gap-0.5">
                                  {d.forecast_rule?.startsWith('per_person') ? <Users className="w-2.5 h-2.5" /> : null}
                                  {ruleLabel}
                                </div>
                              </td>
                              {contributions.map((val, i) => (
                                <td key={buckets[i]} className={cn("p-2 text-right font-mono",
                                  val > 0 && "text-red-500",
                                  val === 0 && "text-muted-foreground/30",
                                  i === cutoffIdx && "border-r-2 border-amber-500",
                                  i > cutoffIdx && "bg-blue-50/20 dark:bg-blue-950/10"
                                )}>{val !== 0 ? fmtMoney(val) : '-'}</td>
                              ))}
                              <td className="p-2 text-right font-mono font-semibold">{fmtMoney(total)}</td>
                              <td className="p-2"></td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Section Total Row */}
              <tr className="border-t-2 border-border font-bold sticky bottom-0 z-20" data-testid={`${sectionKey}-total-row`}>
                <td className={cn("p-2.5 border-r border-border bg-slate-200 dark:bg-slate-800 orka-text-primary", stickyLeftCls, "z-30")}>
                  Total
                </td>
                <td className="p-2.5 bg-slate-200 dark:bg-slate-800"></td>
                {buckets.map((b, i) => {
                  let colTotal = 0;
                  for (const [, items] of actualsBySubType) colTotal += items.reduce((s, item) => s + (item.buckets[b] || 0), 0);
                  colTotal += activeDrivers.reduce((s, d) => s + (computeDriverRow(d, buckets)[i] || 0), 0);
                  return (
                    <td key={b} className={cn("p-2.5 text-right font-mono bg-slate-200 dark:bg-slate-800 orka-text-primary",
                      i === cutoffIdx && "border-r-2 border-amber-500"
                    )}>{colTotal > 0 ? fmtMoney(colTotal) : '-'}</td>
                  );
                })}
                <td className="p-2.5 text-right font-mono bg-slate-200 dark:bg-slate-800 orka-text-primary">
                  {fmtMoney(
                    actualsBySubType.reduce((s, [, items]) => s + items.reduce((a, item) => a + buckets.reduce((b2, bk) => b2 + (item.buckets[bk] || 0), 0), 0), 0) +
                    activeDrivers.reduce((s, d) => s + computeDriverRow(d, buckets).reduce((a, v) => a + v, 0), 0)
                  )}
                </td>
                <td className="p-2.5 bg-slate-200 dark:bg-slate-800"></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground">
          <p className="text-sm">No items yet. Add forecast drivers or record expenses in this category.</p>
        </div>
      )}

      {/* Retired/Cancelled Archive */}
      {driverTab === 'manual' && retiredDrivers.length > 0 && (
        <div className="border-t">
          <button className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-900 flex items-center gap-2 hover:bg-slate-200 dark:hover:bg-slate-800 text-left"
            onClick={() => setShowArchived(!showArchived)} data-testid={`${sectionKey}-archived-toggle`}>
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform text-muted-foreground", !showArchived && "-rotate-90")} />
            <Archive className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground">Retired / Cancelled ({retiredDrivers.length})</span>
          </button>
          {showArchived && (
            <div className="overflow-x-auto max-h-[250px]">
              <table className="w-full text-xs border-collapse">
                <tbody>
                  {retiredDrivers.map((d, idx) => {
                    const contributions = computeDriverRow(d, buckets);
                    const total = contributions.reduce((s, v) => s + v, 0);
                    return (
                      <tr key={d.driver_id || idx} className="border-t border-border/50 hover:bg-muted/10 opacity-60">
                        <td className={cn("p-2 pl-4 border-r border-border bg-card min-w-[220px]", stickyLeftCls)}>
                          <div className="font-medium">{d.tool_name || d.name || 'Unnamed'}</div>
                          {d.vendor_name && <div className="text-[10px] text-muted-foreground">{d.vendor_name}</div>}
                          <Badge variant="outline" className="text-[8px] px-1 py-0 mt-0.5 text-amber-600">Retired</Badge>
                        </td>
                        <td className="p-2 min-w-[80px]"></td>
                        {contributions.map((val, i) => (
                          <td key={buckets[i]} className={cn("p-2 text-right font-mono text-muted-foreground min-w-[85px]",
                            i === cutoffIdx && "border-r-2 border-amber-500"
                          )}>{val !== 0 ? fmtMoney(val) : '-'}</td>
                        ))}
                        <td className="p-2 text-right font-mono font-semibold text-muted-foreground min-w-[90px]">{fmtMoney(total)}</td>
                        <td className="p-2 text-right w-20">
                          <Button variant="ghost" size="icon" className="h-6 w-6" title="Reactivate"
                            onClick={() => reactivateDriver(d.driver_id)}>
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      </>)}

      {/* Automated / Modeled tab */}
      {driverTab === 'automated' && (
        <div className="px-4 py-8 text-center">
          <Cpu className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
          <h4 className="text-sm font-semibold mb-1">Automated Forecast Drivers</h4>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Rules like "per person" cost scaling, contract renewals, and seasonal adjustments
            are configured in each driver's settings. Switch to Manual Lines to manage items.
          </p>
          {activeDrivers.filter(d => d.forecast_rule && d.forecast_rule !== 'fixed').length > 0 ? (
            <div className="mt-4 border rounded-lg overflow-hidden max-w-lg mx-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/30"><tr>
                  <th className="text-left p-2 font-medium">Item</th>
                  <th className="text-left p-2 font-medium">Rule</th>
                  <th className="text-right p-2 font-medium">Base Cost</th>
                </tr></thead>
                <tbody className="divide-y">
                  {activeDrivers.filter(d => d.forecast_rule && d.forecast_rule !== 'fixed').map(d => (
                    <tr key={d.driver_id} className="hover:bg-accent/30">
                      <td className="p-2 font-medium">{d.tool_name || d.name}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px]">
                        {FORECAST_RULES.find(r => r.value === d.forecast_rule)?.label || d.forecast_rule}
                      </Badge></td>
                      <td className="p-2 text-right font-mono">{fmtMoney(d.monthly_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[10px] text-muted-foreground mt-2">No automated rules. Add items with per-person or growth rules in Manual Lines.</p>
          )}
        </div>
      )}

      {/* History tab */}
      {driverTab === 'history' && (
        <div className="px-4 py-8 text-center">
          <Clock className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
          <h4 className="text-sm font-semibold mb-1">Driver Change History</h4>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Audit trail of all changes to forecast drivers in this category.
            Rate changes, additions, retirements, and rule modifications.
          </p>
          {retiredDrivers.length > 0 && (
            <div className="mt-4 border rounded-lg overflow-hidden max-w-lg mx-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/30"><tr>
                  <th className="text-left p-2 font-medium">Item</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-right p-2 font-medium">Last Cost</th>
                </tr></thead>
                <tbody className="divide-y">
                  {retiredDrivers.map(d => (
                    <tr key={d.driver_id} className="hover:bg-accent/30 text-muted-foreground">
                      <td className="p-2">{d.tool_name || d.name}</td>
                      <td className="p-2"><Badge variant="secondary" className="text-[10px]">Retired</Badge></td>
                      <td className="p-2 text-right font-mono">{fmtMoney(d.monthly_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Scenario tab */}
      {driverTab === 'scenario' && (
        <div className="px-4 py-8 text-center">
          <GitBranch className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
          <h4 className="text-sm font-semibold mb-1">Scenario Variants</h4>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Compare cost projections across Base, Conservative, Aggressive, and custom scenarios for this category.
          </p>
          {activeDrivers.filter(d => d.scenario && d.scenario !== 'base').length > 0 ? (
            <div className="mt-4 border rounded-lg overflow-hidden max-w-lg mx-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/30"><tr>
                  <th className="text-left p-2 font-medium">Item</th>
                  <th className="text-left p-2 font-medium">Scenario</th>
                  <th className="text-right p-2 font-medium">Monthly Cost</th>
                </tr></thead>
                <tbody className="divide-y">
                  {activeDrivers.filter(d => d.scenario && d.scenario !== 'base').map(d => (
                    <tr key={d.driver_id} className="hover:bg-accent/30">
                      <td className="p-2 font-medium">{d.tool_name || d.name}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px] capitalize">{d.scenario}</Badge></td>
                      <td className="p-2 text-right font-mono">{fmtMoney(d.monthly_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[10px] text-muted-foreground mt-2">No scenario variants. Use the Add Item dialog and select a scenario other than "Base".</p>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={addModal} onOpenChange={o => { if (!o) { setAddModal(false); setEditingDriver(null); setForm({}); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDriver ? 'Edit' : 'Add'} Item</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground block mb-1">Name *</label>
              <Input value={form.tool_name || form.name || ''} onChange={e => { set('tool_name', e.target.value); set('name', e.target.value); }} className="h-8 text-sm" data-testid="cat-driver-name-input" /></div>
            <div><label className="text-xs text-muted-foreground block mb-1">Sub-Category</label>
              <Input value={form.sub_type || form.tool_category || ''} onChange={e => { set('sub_type', e.target.value); set('tool_category', e.target.value); }} className="h-8 text-sm" /></div>
            <div><label className="text-xs text-muted-foreground block mb-1">Vendor / Supplier</label>
              <div className="relative">
                <Input value={form.vendor_name || ''} onChange={e => set('vendor_name', e.target.value)}
                  className="h-8 text-sm" placeholder="Type or select..." list={`vendor-list-${sectionKey}`} />
                <datalist id={`vendor-list-${sectionKey}`}>
                  {(vendors || []).map(v => <option key={v.vendor_id || v.name} value={v.name} />)}
                </datalist>
              </div>
            </div>
            <div><label className="text-xs text-muted-foreground block mb-1">Monthly Cost *</label>
              <Input type="number" value={form.monthly_cost || ''} onChange={e => set('monthly_cost', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-xs text-muted-foreground block mb-1">Forecast Rule</label>
              <Select value={form.forecast_rule || 'fixed'} onValueChange={v => set('forecast_rule', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{FORECAST_RULES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select></div>
            {form.forecast_rule === 'per_person_team' && (
              <div className="col-span-2"><label className="text-xs text-muted-foreground block mb-1">Linked Teams (comma-separated)</label>
                <Input value={(form.linked_teams || []).join(', ')} onChange={e => set('linked_teams', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  className="h-8 text-sm" placeholder="e.g. Engineering, Design" /></div>
            )}
            {form.forecast_rule === 'per_person_users' && (
              <div className="col-span-2"><label className="text-xs text-muted-foreground block mb-1">Linked Users</label>
                <Input value={(form.linked_users || []).join(', ')} onChange={e => set('linked_users', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  className="h-8 text-sm" placeholder="User IDs or names" /></div>
            )}
            <div><label className="text-xs text-muted-foreground block mb-1">Growth % (MoM)</label>
              <Input type="number" value={form.growth_pct || ''} onChange={e => set('growth_pct', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-xs text-muted-foreground block mb-1">Start Date</label>
              <Input type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-xs text-muted-foreground block mb-1">End Date</label>
              <Input type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-xs text-muted-foreground block mb-1">Scenario</label>
              <Select value={form.scenario || 'base'} onValueChange={v => set('scenario', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{SCENARIOS.map(s => <SelectItem key={s.toLowerCase()} value={s.toLowerCase()}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          {form.forecast_rule === 'per_person_all' && (
            <p className="text-xs text-muted-foreground mt-2">Cost will be multiplied by total staff count ({totalStaffCount} currently).</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModal(false)}>Cancel</Button>
            <Button onClick={saveDriver} data-testid="save-cat-driver-btn">{editingDriver ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
